import { useState, useEffect } from "react";
import { supabase } from "./supabase";

// ── Profile ──────────────────────────────────
export function useProfile(userId) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single()
      .then(({ data, error }) => {
        if (!error && data) setProfile(data);
        setLoading(false);
      });
  }, [userId]);

  const updateProfile = async (updates) => {
    const { data, error } = await supabase
      .from("profiles")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", userId)
      .select()
      .single();
    if (!error && data) setProfile(data);
    return { data, error };
  };

  return { profile, loading, updateProfile };
}

// ── Projects ─────────────────────────────────
export function useProjects(userId) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = async () => {
    if (!userId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("projects")
      .select(`
        *,
        project_updates(*),
        project_files(*),
        approvals(*)
      `)
      .eq("designer_id", userId)
      .order("created_at", { ascending: false });

    if (!error && data) {
      const shaped = data.map(p => ({
        ...p,
        updates: (p.project_updates || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        files: (p.project_files || []).map(f => ({
          name: f.name,
          size: f.size || "—",
          date: f.created_at?.split("T")[0],
          type: f.file_type || "file",
          id: f.id,
          storagePath: f.storage_path,
        })),
        pendingApprovals: (p.approvals || []).filter(a => a.status === "pending"),
      }));
      setProjects(shaped);
    }
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, [userId]);

  const submitApproval = async (approvalId, decision, comment, selectedOption) => {
    const fullComment = selectedOption
      ? `Selected: ${selectedOption}${comment ? ` — ${comment}` : ""}`
      : comment;
    const { error } = await supabase
      .from("approvals")
      .update({
        status: decision === "approve" ? "approved" : "declined",
        decision,
        comment: fullComment,
        decided_at: new Date().toISOString(),
      })
      .eq("id", approvalId);
    if (!error) await fetchProjects();
    return { error };
  };

  return { projects, loading, refetch: fetchProjects, submitApproval };
}

// ── Messages ─────────────────────────────────
export function useMessages(userId) {
  const [messages, setMessages] = useState([]);

  const fetchMessages = async () => {
    if (!userId) return;
    // Get all project IDs the user owns first
    const { data: userProjects } = await supabase
      .from("projects")
      .select("id")
      .eq("designer_id", userId);
    const projectIds = (userProjects || []).map(p => p.id);
    if (projectIds.length === 0) return;
    const { data } = await supabase
      .from("messages")
      .select("*")
      .in("project_id", projectIds)
      .order("created_at", { ascending: true });
    if (data) setMessages(data.map(m => ({
      ...m,
      senderInitial: m.sender_initial || m.sender_name?.charAt(0) || "?",
      time: new Date(m.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      date: isToday(m.created_at) ? "Today" : new Date(m.created_at).toLocaleDateString(),
      projectId: m.project_id,
    })));
  };

  useEffect(() => {
    fetchMessages();

    // Realtime subscription
    const channel = supabase
      .channel("messages_channel")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
      }, (payload) => {
        const m = payload.new;
        setMessages(prev => [...prev, {
          ...m,
          senderInitial: m.sender_initial || m.sender_name?.charAt(0) || "?",
          time: new Date(m.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
          date: "Today",
          projectId: m.project_id,
        }]);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [userId]);

  const sendMessage = async ({ projectId, senderName, senderInitial, isEspion, text, senderId }) => {
    const { error } = await supabase.from("messages").insert({
      project_id: projectId,
      sender_id: senderId || null,
      sender_name: senderName,
      sender_initial: senderInitial,
      is_espion: isEspion || false,
      text,
    });
    return { error };
  };

  return { messages, sendMessage, refetch: fetchMessages };
}

// ── Invoices ─────────────────────────────────
export function useInvoices(userId) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    supabase
      .from("invoices")
      .select("*")
      .eq("designer_id", userId)
      .order("invoice_date", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setInvoices(data.map(inv => ({
          ...inv,
          project: inv.project_name,
          type: inv.invoice_type,
          date: inv.invoice_date,
          dueDate: inv.due_date,
          squareUrl: inv.square_url || "#",
        })));
        setLoading(false);
      });
  }, [userId]);

  return { invoices, loading };
}

// ── Resources ────────────────────────────────
export function useResources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("resources")
      .select("*")
      .eq("published", true)
      .order("category")
      .then(({ data, error }) => {
        if (!error && data) setResources(data.map(r => ({
          ...r,
          cat: r.category,
          desc: r.description,
          size: r.file_size,
          type: r.file_type,
        })));
        setLoading(false);
      });
  }, []);

  return { resources, loading };
}

// ── File Upload ───────────────────────────────
export async function uploadProjectFile(projectId, file, uploadedBy) {
  const ext = file.name.split(".").pop();
  const path = `projects/${projectId}/${Date.now()}_${file.name}`;

  const { error: uploadError } = await supabase.storage
    .from("project-files")
    .upload(path, file);

  if (uploadError) return { error: uploadError };

  const { error: dbError } = await supabase.from("project_files").insert({
    project_id: projectId,
    name: file.name,
    size: formatFileSize(file.size),
    file_type: ext,
    storage_path: path,
    uploaded_by: uploadedBy,
  });

  return { error: dbError };
}

export async function getFileUrl(storagePath) {
  const { data } = supabase.storage
    .from("project-files")
    .getPublicUrl(storagePath);
  return data?.publicUrl;
}

// ── Helpers ───────────────────────────────────
function isToday(dateString) {
  const d = new Date(dateString);
  const today = new Date();
  return d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
